import { Controller, Control, FieldErrors, useWatch } from 'react-hook-form'

import { useTranslation } from '@/core/i18n/useTranslation'
import { ErrorMessage } from '@/shared/components/atoms/ErrorMessage'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import { RadioGroup, RadioGroupItem } from '@/shared/components/atoms/Radio'
import { Textarea } from '@/shared/components/atoms/TextArea'

interface NetworkConfigStepProps {
	control: Control<any>
	errors: FieldErrors
	isLoading?: boolean
	fieldPrefix?: 'store' | 'box.connection_settings'
}

export const NetworkConfigStep: React.FC<NetworkConfigStepProps> = ({
	control,
	errors: _errors,
	isLoading = false,
	fieldPrefix = 'store',
}) => {
	const { t } = useTranslation('zones')

	// Helper to build field names based on prefix and API structure
	const getFieldName = (fieldName: string) => {
		if (fieldPrefix === 'box.connection_settings') {
			// Map to box connection_settings structure
			const fieldMap: Record<string, string> = {
				network_type: 'connection_type',
				wifi_network: 'network',
				wifi_password: 'password',
				network_encryption: 'type_of_network',
				ip_type: 'ip_protocol_assignment',
				ip_address: 'ip_address',
				dns_1: 'dns_1',
				dns_2: 'dns_2',
				gateway: 'gateway',
				netmask: 'netmask',
				network_additional_info: 'additional_information',
			}
			return `${fieldPrefix}.${fieldMap[fieldName] || fieldName}`
		}
		// For 'store', use direct field names
		return `${fieldPrefix}.${fieldName}`
	}

	const networkType = useWatch({
		control,
		name: getFieldName('network_type'),
		defaultValue: fieldPrefix === 'box.connection_settings' ? 'WIFI' : 'wifi',
	})

	const ipType = useWatch({
		control,
		name: getFieldName('ip_type'),
		defaultValue: fieldPrefix === 'box.connection_settings' ? 'DHCP' : 'DHCP',
	})

	const isWifi =
		networkType === 'wifi' || networkType === 'WIFI' || networkType === null
	const isStaticIP = ipType === 'Static IP' || ipType === 'STATIC'

	// Value mappings for box vs store
	const wifiValue = fieldPrefix === 'box.connection_settings' ? 'WIFI' : 'wifi'
	const ethernetValue =
		fieldPrefix === 'box.connection_settings' ? 'ETHERNET' : 'ethernet'
	const dhcpValue = fieldPrefix === 'box.connection_settings' ? 'DHCP' : 'DHCP'
	const staticValue =
		fieldPrefix === 'box.connection_settings' ? 'STATIC' : 'Static IP'

	return (
		<div className="space-y-6">
			{/* Network Type */}
			<div>
				<Label className="mb-2 block">
					{t('create.networkForm.networkSettings')}
				</Label>
				<Controller
					name={getFieldName('network_type')}
					control={control}
					render={({ field }) => (
						<RadioGroup
							value={field.value || wifiValue}
							onValueChange={field.onChange}
							className="flex gap-4"
						>
							<div className="flex items-center space-x-2">
								<RadioGroupItem
									value={wifiValue}
									id="wifi"
									disabled={isLoading}
								/>
								<Label
									htmlFor="wifi"
									className="mb-0 cursor-pointer font-normal"
								>
									{t('create.networkForm.wifi')}
								</Label>
							</div>
							<div className="flex items-center space-x-2">
								<RadioGroupItem
									value={ethernetValue}
									id="ethernet"
									disabled={isLoading}
								/>
								<Label
									htmlFor="ethernet"
									className="mb-0 cursor-pointer font-normal"
								>
									{t('create.networkForm.ethernet')}
								</Label>
							</div>
						</RadioGroup>
					)}
				/>
			</div>

			{/* WiFi Network Name (only if WiFi is selected) */}
			{isWifi && (
				<div>
					<Label className="mb-2 block">
						{t('create.networkForm.wifiNetwork')}
					</Label>
					<Controller
						name={getFieldName('wifi_network')}
						control={control}
						rules={{
							required: isWifi
								? t('create.networkForm.validation.wifiNetworkRequired')
								: false,
						}}
						render={({ field, fieldState: { error } }) => (
							<div>
								<Input
									{...field}
									value={field.value || ''}
									placeholder={t('create.networkForm.placeholders.wifiNetwork')}
									disabled={isLoading}
									className="h-10 text-sm"
								/>
								{error && <ErrorMessage error={error.message ?? ''} />}
							</div>
						)}
					/>
				</div>
			)}

			{/* WiFi Password (only if WiFi is selected) */}
			{isWifi && (
				<div>
					<Label className="mb-2 block">
						{t('create.networkForm.wifiPassword')}
					</Label>
					<Controller
						name={getFieldName('wifi_password')}
						control={control}
						rules={{
							required: isWifi
								? t('create.networkForm.validation.wifiPasswordRequired')
								: false,
						}}
						render={({ field, fieldState: { error } }) => (
							<div>
								<Input
									{...field}
									value={field.value || ''}
									type="password"
									placeholder={t(
										'create.networkForm.placeholders.wifiPassword'
									)}
									disabled={isLoading}
									className="h-10 text-sm"
								/>
								{error && <ErrorMessage error={error.message ?? ''} />}
							</div>
						)}
					/>
				</div>
			)}

			{/* Network Encryption Type (only if WiFi is selected) */}
			{isWifi && (
				<div>
					<Label className="mb-2 block">
						{t('create.networkForm.networkEncryption')}
					</Label>
					<Controller
						name={getFieldName('network_encryption')}
						control={control}
						render={({ field }) => (
							<RadioGroup
								value={field.value || 'WEP'}
								onValueChange={field.onChange}
								className="flex gap-4"
							>
								<div className="flex items-center space-x-2">
									<RadioGroupItem value="WEP" id="wep" disabled={isLoading} />
									<Label
										htmlFor="wep"
										className="mb-0 cursor-pointer font-normal"
									>
										{t('create.networkForm.encryption.wep')}
									</Label>
								</div>
								<div className="flex items-center space-x-2">
									<RadioGroupItem value="WPA2" id="wpa2" disabled={isLoading} />
									<Label
										htmlFor="wpa2"
										className="mb-0 cursor-pointer font-normal"
									>
										{t('create.networkForm.encryption.wpa2')}
									</Label>
								</div>
							</RadioGroup>
						)}
					/>
				</div>
			)}

			{/* IP Configuration */}
			<div>
				<Label className="mb-2 block">
					{t('create.networkForm.ipConfiguration')}
				</Label>
				<Controller
					name={getFieldName('ip_type')}
					control={control}
					render={({ field }) => (
						<RadioGroup
							value={field.value || dhcpValue}
							onValueChange={field.onChange}
							className="flex gap-4"
						>
							<div className="flex items-center space-x-2">
								<RadioGroupItem
									value={dhcpValue}
									id="dhcp"
									disabled={isLoading}
								/>
								<Label
									htmlFor="dhcp"
									className="mb-0 cursor-pointer font-normal"
								>
									{t('create.networkForm.dhcp')}
								</Label>
							</div>
							<div className="flex items-center space-x-2">
								<RadioGroupItem
									value={staticValue}
									id="static"
									disabled={isLoading}
								/>
								<Label
									htmlFor="static"
									className="mb-0 cursor-pointer font-normal"
								>
									{t('create.networkForm.staticIp')}
								</Label>
							</div>
						</RadioGroup>
					)}
				/>
			</div>

			{/* Advanced IP Settings (only if Static IP is selected) */}
			{isStaticIP && (
				<>
					<div className="border-t border-border pt-6">
						<h3 className="text-base font-medium mb-4">
							{t('create.networkForm.advancedIpConfig')}
						</h3>
						<div className="space-y-6">
							{/* Static IP Address */}
							<div>
								<Label className="mb-2 block">
									{t('create.networkForm.ipAddress')}
								</Label>
								<Controller
									name={getFieldName('ip_address')}
									control={control}
									rules={{
										required: isStaticIP
											? t('create.networkForm.validation.ipAddressRequired')
											: false,
										pattern: {
											value: /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/,
											message: t(
												'create.networkForm.validation.ipAddressInvalid'
											),
										},
									}}
									render={({ field, fieldState: { error } }) => (
										<div>
											<Input
												{...field}
												value={field.value || ''}
												placeholder={t(
													'create.networkForm.placeholders.ipAddress'
												)}
												disabled={isLoading}
												className="h-10 text-sm"
											/>
											{error && <ErrorMessage error={error.message ?? ''} />}
										</div>
									)}
								/>
							</div>

							{/* Subnet Mask */}
							<div>
								<Label className="mb-2 block">
									{t('create.networkForm.subnetMask')}
								</Label>
								<Controller
									name={getFieldName('netmask')}
									control={control}
									rules={{
										required: isStaticIP
											? t('create.networkForm.validation.subnetMaskRequired')
											: false,
										pattern: {
											value: /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/,
											message: t(
												'create.networkForm.validation.subnetMaskInvalid'
											),
										},
									}}
									render={({ field, fieldState: { error } }) => (
										<div>
											<Input
												{...field}
												value={field.value || ''}
												placeholder={t(
													'create.networkForm.placeholders.subnetMask'
												)}
												disabled={isLoading}
												className="h-10 text-sm"
											/>
											{error && <ErrorMessage error={error.message ?? ''} />}
										</div>
									)}
								/>
							</div>

							{/* Gateway */}
							<div>
								<Label className="mb-2 block">
									{t('create.networkForm.gateway')}
								</Label>
								<Controller
									name={getFieldName('gateway')}
									control={control}
									rules={{
										required: isStaticIP
											? t('create.networkForm.validation.gatewayRequired')
											: false,
										pattern: {
											value: /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/,
											message: t(
												'create.networkForm.validation.gatewayInvalid'
											),
										},
									}}
									render={({ field, fieldState: { error } }) => (
										<div>
											<Input
												{...field}
												value={field.value || ''}
												placeholder={t(
													'create.networkForm.placeholders.gateway'
												)}
												disabled={isLoading}
												className="h-10 text-sm"
											/>
											{error && <ErrorMessage error={error.message ?? ''} />}
										</div>
									)}
								/>
							</div>

							{/* DNS Servers */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div>
									<Label className="mb-2 block">
										{t('create.networkForm.primaryDns')}
									</Label>
									<Controller
										name={getFieldName('dns_1')}
										control={control}
										rules={{
											required: isStaticIP
												? t('create.networkForm.validation.primaryDnsRequired')
												: false,
											pattern: {
												value: /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/,
												message: t('create.networkForm.validation.dnsInvalid'),
											},
										}}
										render={({ field, fieldState: { error } }) => (
											<div>
												<Input
													{...field}
													value={field.value || ''}
													placeholder={t(
														'create.networkForm.placeholders.primaryDns'
													)}
													disabled={isLoading}
													className="h-10 text-sm"
												/>
												{error && <ErrorMessage error={error.message ?? ''} />}
											</div>
										)}
									/>
								</div>

								<div>
									<Label className="mb-2 block">
										{t('create.networkForm.secondaryDns')}
									</Label>
									<Controller
										name={getFieldName('dns_2')}
										control={control}
										rules={{
											pattern: {
												value: /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/,
												message: t(
													'create.networkForm.validation.secondaryDnsInvalid'
												),
											},
										}}
										render={({ field, fieldState: { error } }) => (
											<div>
												<Input
													{...field}
													value={field.value || ''}
													placeholder={t(
														'create.networkForm.placeholders.secondaryDns'
													)}
													disabled={isLoading}
													className="h-10 text-sm"
												/>
												{error && <ErrorMessage error={error.message ?? ''} />}
											</div>
										)}
									/>
								</div>
							</div>
						</div>
					</div>
				</>
			)}

			{/* Additional Information */}
			<div>
				<Label className="mb-2 block">
					{t('create.networkForm.additionalInfo')}
				</Label>
				<Controller
					name={getFieldName('network_additional_info')}
					control={control}
					render={({ field, fieldState: { error } }) => (
						<div>
							<Textarea
								{...field}
								value={field.value || ''}
								placeholder={t(
									'create.networkForm.placeholders.additionalInfo'
								)}
								disabled={isLoading}
								rows={3}
								className="text-sm bg-background border-border text-foreground resize-none"
							/>
							{error && <ErrorMessage error={error.message ?? ''} />}
						</div>
					)}
				/>
			</div>
		</div>
	)
}
